import { ChangeDetectionStrategy, Component, input, model } from '@angular/core';

export interface TabItem {
  value: string;
  label: string;
  count?: number | string | null;
  disabled?: boolean;
  disabledReason?: string;
}

/**
 * Guide v1.1 tabs: views of one thing. 14 px label, ink when active, a 2 px blue
 * bar on the baseline and an optional mono count. The only tab style in the Hub;
 * it replaces segmented controls and filter pills.
 */
@Component({
  selector: 'app-tabs',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block overflow-x-auto' },
  template: `
    <nav
      class="flex min-w-max gap-6"
      [class.border-b]="ruled()"
      [class.border-border]="ruled()"
      role="tablist"
      [attr.aria-label]="label()"
      (keydown)="onKey($event)"
    >
      @for (tab of items(); track tab.value) {
        <button
          type="button"
          role="tab"
          class="relative inline-flex h-11 items-center gap-1.5 text-sm font-normal transition-colors focus-visible:outline-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:text-muted-foreground/40"
          [class]="value() === tab.value ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'"
          [attr.aria-selected]="value() === tab.value"
          [attr.tabindex]="value() === tab.value ? 0 : -1"
          [attr.title]="tab.disabled ? (tab.disabledReason ?? null) : null"
          [disabled]="tab.disabled"
          (click)="value.set(tab.value)"
        >
          {{ tab.label }}
          @if (tab.count !== undefined && tab.count !== null) {
            <span
              class="rounded-full px-1.5 py-0.5 font-mono text-2xs leading-none"
              [class]="value() === tab.value ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'"
            >{{ tab.count }}</span>
          }
          @if (value() === tab.value) {
            <span class="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-primary" aria-hidden="true"></span>
          }
        </button>
      }
    </nav>
  `,
})
export class Tabs {
  items = input.required<readonly TabItem[]>();
  value = model<string>();
  label = input('Ansichten');
  /** Hairline under the tabs; off when the parent already draws one (page header). */
  ruled = input(true);

  /** Arrow keys move between enabled tabs (WAI-ARIA tabs pattern). */
  onKey(event: KeyboardEvent): void {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    const enabled = this.items().filter((t) => !t.disabled);
    const index = enabled.findIndex((t) => t.value === this.value());
    const next = enabled[(index + (event.key === 'ArrowRight' ? 1 : -1) + enabled.length) % enabled.length];
    if (!next) return;
    event.preventDefault();
    this.value.set(next.value);
    const buttons = (event.currentTarget as HTMLElement).querySelectorAll<HTMLButtonElement>('[role=tab]');
    buttons[this.items().indexOf(next)]?.focus();
  }
}
